import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import { collection, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import {
  downloadImageUrl,
  filenameForGalleryAsset,
} from '../../lib/downloadImageUrl';
import { listImageEditorDraftsForOpen } from '../../lib/imageEditorDraftsFiles';
import { IMAGE_EDITOR_PROJECT_KIND } from '../../lib/imageEditorProjectFormat';

/**
 * @param {unknown} asset
 * @returns {string}
 */
function assetImageUrl(asset) {
  if (!asset || typeof asset !== 'object') return '';
  const preview =
    typeof asset.previewUrl === 'string' ? asset.previewUrl.trim() : '';
  const image = typeof asset.imageUrl === 'string' ? asset.imageUrl.trim() : '';
  return preview || image || '';
}

/**
 * @param {unknown} asset
 * @returns {string}
 */
function assetLabel(asset) {
  if (!asset || typeof asset !== 'object') return 'asset';
  if (typeof asset.assetName === 'string' && asset.assetName.trim()) {
    return asset.assetName.trim();
  }
  if (typeof asset.manifestAssetId === 'string' && asset.manifestAssetId.trim()) {
    return asset.manifestAssetId.trim();
  }
  if (typeof asset.id === 'string' && asset.id.trim()) return asset.id.trim();
  return 'asset';
}

/**
 * @param {{
 *   items: object[],
 *   emptyMessage: string,
 *   badge?: (item: object) => string,
 *   onOpen: (item: object) => void,
 *   onDownloadError?: (message: string) => void,
 * }} props
 */
function AssetThumbGrid({
  items,
  emptyMessage,
  badge,
  onOpen,
  onDownloadError,
}) {
  if (!items.length) {
    return <Alert severity="info">{emptyMessage}</Alert>;
  }
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
        gap: 1.25,
      }}
    >
      {items.map((asset) => {
        const url = assetImageUrl(asset);
        const label = assetLabel(asset);
        return (
          <Box
            key={asset.id}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 1,
              bgcolor: 'background.paper',
              textAlign: 'left',
              '&:hover': { borderColor: 'primary.main' },
            }}
          >
            <Box
              component="button"
              type="button"
              onClick={() => onOpen(asset)}
              sx={{
                display: 'block',
                width: '100%',
                p: 0,
                border: 0,
                bgcolor: 'transparent',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <Box
                component="img"
                src={url}
                alt={label}
                sx={{
                  width: '100%',
                  height: 96,
                  objectFit: 'contain',
                  bgcolor: 'grey.900',
                  borderRadius: 0.5,
                  display: 'block',
                }}
              />
              <Typography
                variant="caption"
                noWrap
                display="block"
                sx={{ mt: 0.75, fontWeight: 600 }}
                title={label}
              >
                {label}
              </Typography>
              {badge ? (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {badge(asset)}
                </Typography>
              ) : null}
            </Box>
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 0.5 }}>
              <Tooltip title="Download">
                <span>
                  <IconButton
                    size="small"
                    disabled={!url}
                    aria-label={`Download ${label}`}
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (!url) return;
                      try {
                        await downloadImageUrl(
                          url,
                          filenameForGalleryAsset(asset),
                        );
                      } catch (err) {
                        onDownloadError?.(
                          err?.message || 'Download failed.',
                        );
                      }
                    }}
                    sx={{ p: 0.35 }}
                  >
                    <DownloadOutlinedIcon fontSize="inherit" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
}

/**
 * Open dialog — Project gallery / Drafts / local file.
 *
 * @param {{
 *   open: boolean,
 *   companyId: string,
 *   projects: Array<{ id: string, title?: string }>,
 *   projectsLoading?: boolean,
 *   busy?: boolean,
 *   onCancel: () => void,
 *   onOpenGalleryAsset: (asset: object, projectId: string) => void,
 *   onOpenDraft: (draft: object) => void,
 *   onOpenLocalFile: (file: File) => void,
 * }} props
 */
export default function ImageEditorOpenDialog({
  open,
  companyId,
  projects = [],
  projectsLoading = false,
  busy = false,
  onCancel,
  onOpenGalleryAsset,
  onOpenDraft,
  onOpenLocalFile,
}) {
  const fileInputRef = useRef(null);
  const [tab, setTab] = useState(0);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [galleryAssets, setGalleryAssets] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryError, setGalleryError] = useState('');
  const [drafts, setDrafts] = useState([]);
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [draftsError, setDraftsError] = useState('');

  useEffect(() => {
    if (!open) return;
    setTab(0);
    setGalleryError('');
    setDraftsError('');
    setSelectedProjectId((prev) => {
      if (prev && projects.some((p) => p.id === prev)) return prev;
      return projects[0]?.id || '';
    });
  }, [open, projects]);

  useEffect(() => {
    if (!open || !companyId || !selectedProjectId || tab !== 0) {
      if (tab !== 0) setGalleryAssets([]);
      return undefined;
    }
    setGalleryLoading(true);
    setGalleryError('');
    const assetsCol = collection(
      db,
      'companies',
      companyId,
      'game_projects',
      selectedProjectId,
      'assets',
    );
    const unsub = onSnapshot(
      assetsCol,
      (snap) => {
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((a) => Boolean(assetImageUrl(a)))
          .sort((a, b) => {
            const aMs =
              a.createdAt?.toMillis?.() || a.createdAt?.seconds * 1000 || 0;
            const bMs =
              b.createdAt?.toMillis?.() || b.createdAt?.seconds * 1000 || 0;
            return bMs - aMs;
          });
        setGalleryAssets(rows);
        setGalleryLoading(false);
      },
      (err) => {
        console.error('ImageEditorOpenDialog gallery:', err);
        setGalleryError(err.message || 'Could not load project gallery.');
        setGalleryAssets([]);
        setGalleryLoading(false);
      },
    );
    return () => unsub();
  }, [open, companyId, selectedProjectId, tab]);

  useEffect(() => {
    if (!open || !companyId || tab !== 1) {
      if (tab !== 1) setDrafts([]);
      return undefined;
    }
    let cancelled = false;
    setDraftsLoading(true);
    setDraftsError('');
    (async () => {
      try {
        // Lazy-migrates legacy drafts into Company Files / Library / Image Editor,
        // then lists Files nodes with a temporary image_editor_drafts read-fallback.
        const rows = await listImageEditorDraftsForOpen({
          companyId,
          uid: auth.currentUser?.uid || null,
        });
        if (cancelled) return;
        setDrafts(rows);
        setDraftsLoading(false);
      } catch (err) {
        console.error('ImageEditorOpenDialog drafts:', err);
        if (cancelled) return;
        setDraftsError(err?.message || 'Could not load drafts.');
        setDrafts([]);
        setDraftsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, companyId, tab]);

  const projectOptions = useMemo(() => projects, [projects]);

  return (
    <Dialog
      open={Boolean(open)}
      onClose={busy ? undefined : onCancel}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Open image</DialogTitle>
      <DialogContent dividers>
        <Tabs
          value={tab}
          onChange={(_e, next) => setTab(next)}
          sx={{ mb: 1.5 }}
        >
          <Tab label="Project gallery" />
          <Tab label="Files" />
          <Tab label="Local image" />
        </Tabs>

        {tab === 0 ? (
          <Stack spacing={1.5}>
            <FormControl size="small" sx={{ minWidth: 240, maxWidth: 360 }}>
              <InputLabel id="open-project-label">Game project</InputLabel>
              <Select
                labelId="open-project-label"
                label="Game project"
                value={selectedProjectId}
                onChange={(e) => setSelectedProjectId(e.target.value)}
                disabled={projectsLoading || !projectOptions.length}
              >
                {projectOptions.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {typeof p.title === 'string' && p.title.trim()
                      ? p.title.trim()
                      : p.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {galleryError ? (
              <Alert severity="warning">{galleryError}</Alert>
            ) : null}
            {!selectedProjectId ? (
              <Alert severity="info">
                Select a game project to browse its gallery.
              </Alert>
            ) : galleryLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <AssetThumbGrid
                items={galleryAssets}
                emptyMessage="No gallery images in this project yet."
                badge={(asset) =>
                  asset.manifestId ? 'Manifest-linked' : 'Gallery'
                }
                onOpen={(asset) => onOpenGalleryAsset(asset, selectedProjectId)}
                onDownloadError={setGalleryError}
              />
            )}
          </Stack>
        ) : null}

        {tab === 1 ? (
          <Stack spacing={1.5}>
            {draftsError ? (
              <Alert severity="warning">{draftsError}</Alert>
            ) : null}
            {draftsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <AssetThumbGrid
                items={drafts}
                emptyMessage="No Image Editor files yet. Save an image or layered project to Files to reopen here."
                badge={(asset) => {
                  if (asset.fromLegacyFallback) return 'Legacy';
                  if (asset.editorDocumentKind === IMAGE_EDITOR_PROJECT_KIND) {
                    const layers =
                      typeof asset.layerCount === 'number'
                        ? `${asset.layerCount} layers`
                        : 'Layered project';
                    return asset.autosave ? `${layers} · autosave` : layers;
                  }
                  if (typeof asset.format === 'string' && asset.format) {
                    return String(asset.format).toUpperCase();
                  }
                  return 'Files';
                }}
                onOpen={onOpenDraft}
                onDownloadError={setDraftsError}
              />
            )}
          </Stack>
        ) : null}

        {tab === 2 ? (
          <Stack spacing={1.5} alignItems="flex-start">
            <Typography variant="body2" color="text.secondary">
              Open a PNG, JPEG, WebP, or other image from your computer. Saves
              without a project go to Files (Library → Image Editor).
            </Typography>
            <Button
              variant="contained"
              startIcon={<FolderOpenOutlinedIcon />}
              disabled={busy}
              onClick={() => fileInputRef.current?.click()}
              sx={{ textTransform: 'none' }}
            >
              Choose local image…
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) onOpenLocalFile(file);
              }}
            />
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
